import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { FEATURE_CONSTANTS } from '../../constants/featureConstants';
import { ENTITY_SECURITY_PERMISSIONS, FEATURE_SECURITY_PERMISSIONS, SECURITY_ACTIONS } from './constants';
import {
    EntityActionPermissionMappingError,
    FeatureActionPermissionMappingError,
    FeaturePermissionTokenValidationError
} from './errors';
import type {
    EntityName,
    EntitySecurityPermission,
    FeatureName,
    FeatureSecurityPermission,
    SecurityAction
} from './types';

export type FeatureSecurityPermissionsMap = Partial<Record<FeatureSecurityPermission, boolean>>;

type SecurityMembershipAccess = {
    feature: string;
    application: string;
    permissions: string[];
};

type SecurityMembership = {
    id: string;
    access?: SecurityMembershipAccess[];
};

type SecurityUserProfile = {
    memberships?: SecurityMembership[];
};

type SecurityApplication = {
    featureApps: string[];
};

const FEATURE_PERMISSION_SET = new Set<FeatureSecurityPermission>([
    FEATURE_SECURITY_PERMISSIONS.READ,
    FEATURE_SECURITY_PERMISSIONS.ANNOTATE,
    FEATURE_SECURITY_PERMISSIONS.MANAGE
]);

const isFeatureSecurityPermission = (permission: string): permission is FeatureSecurityPermission => {
    return FEATURE_PERMISSION_SET.has(permission as FeatureSecurityPermission);
};

const mapPermissionsToFlags = (permissions: string[]): FeatureSecurityPermissionsMap => {
    const permissionFlags: FeatureSecurityPermissionsMap = {};
    const unknownPermissions: string[] = [];

    permissions.forEach((permission) => {
        if (isFeatureSecurityPermission(permission)) {
            permissionFlags[permission] = true;
            return;
        }

        unknownPermissions.push(permission);
    });

    if (unknownPermissions.length > 0) {
        throw new FeaturePermissionTokenValidationError(unknownPermissions);
    }

    return permissionFlags;
};

const ACTION_TO_FEATURE_PERMISSION: Record<
    Exclude<SecurityAction, 'EDIT' | 'DELETE' | 'SHARE'>,
    FeatureSecurityPermission
> = {
    READ: FEATURE_SECURITY_PERMISSIONS.READ,
    COMMENT: FEATURE_SECURITY_PERMISSIONS.ANNOTATE,
    ACTIVATE: FEATURE_SECURITY_PERMISSIONS.MANAGE,
    INACTIVATE: FEATURE_SECURITY_PERMISSIONS.MANAGE
};

const MUTATIVE_ACTIONS = new Set<SecurityAction>([
    SECURITY_ACTIONS.EDIT,
    SECURITY_ACTIONS.DELETE,
    SECURITY_ACTIONS.SHARE
]);

const MIN_FEATURE_PERMISSION_FOR_ENTITY_MUTATION: Partial<Record<EntityName, FeatureSecurityPermission>> = {
    [ENTITY_CONSTANTS.note]: FEATURE_SECURITY_PERMISSIONS.ANNOTATE,
    [ENTITY_CONSTANTS.attachment]: FEATURE_SECURITY_PERMISSIONS.ANNOTATE
};

const ACTION_TO_ENTITY_PERMISSION: Record<SecurityAction, EntitySecurityPermission> = {
    READ: ENTITY_SECURITY_PERMISSIONS.READ,
    EDIT: ENTITY_SECURITY_PERMISSIONS.EDIT,
    DELETE: ENTITY_SECURITY_PERMISSIONS.DELETE,
    COMMENT: ENTITY_SECURITY_PERMISSIONS.COMMENT,
    SHARE: ENTITY_SECURITY_PERMISSIONS.SHARE,
    ACTIVATE: ENTITY_SECURITY_PERMISSIONS.ACTIVATE,
    INACTIVATE: ENTITY_SECURITY_PERMISSIONS.INACTIVATE
};

export const resolveFeaturePermissionFromAction = (
    action: SecurityAction,
    entityName?: EntityName
): FeatureSecurityPermission => {
    if (MUTATIVE_ACTIONS.has(action)) {
        if (entityName) {
            return MIN_FEATURE_PERMISSION_FOR_ENTITY_MUTATION[entityName] ?? FEATURE_SECURITY_PERMISSIONS.MANAGE;
        }

        return FEATURE_SECURITY_PERMISSIONS.MANAGE;
    }

    if (Object.prototype.hasOwnProperty.call(ACTION_TO_FEATURE_PERMISSION, action)) {
        return ACTION_TO_FEATURE_PERMISSION[action];
    }

    throw new FeatureActionPermissionMappingError(action);
};

export const resolveEntityPermissionFromAction = (action: SecurityAction): EntitySecurityPermission => {
    if (Object.prototype.hasOwnProperty.call(ACTION_TO_ENTITY_PERMISSION, action)) {
        return ACTION_TO_ENTITY_PERMISSION[action];
    }

    throw new EntityActionPermissionMappingError(action);
};

export const isFeaturePermissionAllowed = (
    permissions: FeatureSecurityPermissionsMap,
    permission: FeatureSecurityPermission
): boolean => {
    if (permissions?.[permission]) {
        return true;
    }

    const wantsToAnnotate = permission === FEATURE_SECURITY_PERMISSIONS.ANNOTATE;
    const wantsToRead = permission === FEATURE_SECURITY_PERMISSIONS.READ;
    const canAnnotate = Boolean(permissions?.[FEATURE_SECURITY_PERMISSIONS.ANNOTATE]);
    const canManage = Boolean(permissions?.[FEATURE_SECURITY_PERMISSIONS.MANAGE]);

    if (wantsToAnnotate && canManage) {
        return true;
    }

    if (wantsToRead && (canAnnotate || canManage)) {
        return true;
    }

    return false;
};

export const resolveFeaturePermissionsForSecurity = (
    userProfile: SecurityUserProfile,
    application: SecurityApplication,
    activeOrganizationAccount: string,
    featureName: string
) => {
    let featurePermissions: FeatureSecurityPermissionsMap = {};

    if (application && featureName && activeOrganizationAccount && userProfile?.memberships) {
        const activeMembership = userProfile.memberships.find(
            (membership) => membership.id === activeOrganizationAccount
        );

        if (activeMembership?.access) {
            const matchingAccess = application.featureApps
                .map((featureApp) => {
                    const access = activeMembership.access?.find(
                        (accessEntry) => accessEntry.application === featureApp && accessEntry.feature === featureName
                    );

                    return access;
                })
                .find(Boolean);

            if (matchingAccess?.permissions) {
                featurePermissions = mapPermissionsToFlags(matchingAccess.permissions);
            }
        }
    }

    return featurePermissions;
};

const ENTITY_NAMES = new Set<string>(Object.values(ENTITY_CONSTANTS));
const FEATURE_NAMES = new Set<string>(Object.values(FEATURE_CONSTANTS));

export const isEntityName = (value: string | null | undefined): value is EntityName => {
    return Boolean(value && ENTITY_NAMES.has(value));
};

export const isFeatureName = (value: string | null | undefined): value is FeatureName => {
    return Boolean(value && FEATURE_NAMES.has(value));
};
